import {DataTableColumn} from 'mantine-datatable';
import {NavLink} from 'react-router-dom';
import {I18n} from '@aws-amplify/core';
import {
    Code,
    Text,
} from '@mantine/core';

import {formatTimestamp} from '../../../utils/TableTimestamps.tsx';
import {TestRun} from '../types.ts';
import {TestRunStatusBadge} from '../profile/TestRunStatusBadge.tsx';
import {StepCountsSummary} from '../profile/StepCountsSummary.tsx';
import config from '../config';

export const useTestRunsTableColumns = () => {

    const columns: DataTableColumn<TestRun>[] = [
        {
            accessor: 'testCode',
            title: I18n.get('Test'),
            width: '1fr',
            sortable: true,
            // Shows the denormalised testCode, falling back to the raw
            // testDefinitionId. The fallback keeps a run whose code was never
            // captured VISIBLE and still navigable — a blank cell would read as
            // "no test", which is impossible (testDefinitionId is NonNull).
            render: (item: TestRun) => (
                <NavLink to={`${config.basePath}/${item.id}`}>
                    <Code>{item.testCode ?? item.testDefinitionId}</Code>
                </NavLink>
            ),
        },
        {
            accessor: 'status',
            title: I18n.get('Status'),
            width: '130px',
            sortable: true,
            render: (item: TestRun) => <TestRunStatusBadge status={item.status}/>,
        },
        {
            accessor: 'stepsTotal',
            title: I18n.get('Steps'),
            width: '160px',
            // Not sortable: the cell shows a composite of four separate counts,
            // so an accessor-based sort on stepsTotal alone would order the table
            // by something other than what the column appears to show.
            render: (item: TestRun) => <StepCountsSummary record={item}/>,
        },
        {
            accessor: 'startedAt',
            title: I18n.get('Started'),
            width: '1fr',
            sortable: true,
            render: (item: TestRun) => formatTimestamp(item.startedAt),
        },
        {
            accessor: 'completedAt',
            title: I18n.get('Completed'),
            width: '1fr',
            sortable: true,
            // NULL while the run is in flight. Rendered as an explicit dash-free
            // word rather than an empty cell so "still running" is stated rather
            // than inferred from blankness.
            render: (item: TestRun) => item.completedAt
                                       ? formatTimestamp(item.completedAt)
                                       : <Text size={'xs'} c={'dimmed'}>in flight</Text>,
        },
    ];

    return {
        columns,
    };
};
